/**
 * ⭐⭐⭐ **Persediaan & resep** — taksonomi pergerakan stok (§9, VTEC §8b).
 *
 * ### ⛔⛔ Satu pembedaan yang menentukan seluruh modul: AKTUAL vs TEORETIS
 * Penerimaan barang, susut yang dicatat, dan opname adalah **fakta** — seseorang melihat barangnya.
 * Pengurangan lewat resep saat penjualan **bukan**: ia hitungan dari asumsi bahwa tiap porsi
 * memakai persis sebanyak yang tertulis di resep. Rendemen berubah, ada yang tumpah, ada makan
 * karyawan.
 *
 * ⭐ Dan **selisih teoretis-vs-aktual itulah PRODUKNYA**, bukan kesalahan yang harus disembunyikan:
 * ia yang menjawab "dapur ini boros di mana". Begitu backflush dipaksa disebut "aktual", selisih itu
 * lenyap — dan bersamanya satu-satunya angka yang bisa mengelola dapur.
 *
 * ⚠️ Karena itu `is_theoretical` bukan kolom kenyamanan; ia **konsekuensi jenis pergerakan**, dan
 * dipetakan di sini supaya tak ada satu pun tempat yang bisa menuliskannya berbeda (Guardrail #2).
 */
export const STOCK_MOVEMENT_TYPES = Object.freeze([
    /** Barang datang dari vendor / dapur pusat. **AKTUAL.** */
    'receipt',
    /** Pengurangan lewat RESEP saat penjualan lunas. ⚠️ **TEORETIS** — lihat catatan modul. */
    'sale_backflush',
    /**
     * Pengurangan LANGSUNG 1:1 saat penjualan lunas (botolan, SKU retail). **AKTUAL.**
     *
     * ⛔ Jenis tersendiri, bukan `sale_backflush` ber-`is_theoretical = false`: yang membedakan
     * bukan tanda melainkan **cara angkanya lahir**, dan klasifikasi begitu wajib struktural.
     * Botol yang keluar dari kulkas benar-benar terhitung satu; porsi nasi goreng tidak.
     */
    'sale_direct',
    /** Susut yang DICATAT: tumpah, kedaluwarsa, rusak. **AKTUAL** — seseorang melihatnya. */
    'waste',
    'transfer_in',
    'transfer_out',
    /** Hasil hitung fisik. **AKTUAL, dan inilah yang MENGOREKSI.** */
    'opname_adjustment',
]);
const TYPE_SET = new Set(STOCK_MOVEMENT_TYPES);
export function isStockMovementType(v) {
    return typeof v === 'string' && TYPE_SET.has(v);
}
/**
 * ⛔⛔ **Satu-satunya tempat yang memutuskan sebuah pergerakan teoretis atau bukan.**
 *
 * Menyerahkannya ke pemanggil berarti dua jalur (portal & outlet-server) bisa menandai pergerakan
 * yang sama secara berbeda — dan laporan selisih dapur jadi menjumlahkan dua populasi yang bukan
 * apa-apa.
 */
export function isTheoreticalMovement(t) {
    return t === 'sale_backflush';
}
/** Arah tanda kuantitas: `+1` menambah saldo, `-1` mengurangi. */
export function movementSign(t) {
    switch (t) {
        case 'receipt':
        case 'transfer_in':
            return 1;
        case 'sale_backflush':
        case 'sale_direct':
        case 'waste':
        case 'transfer_out':
            return -1;
        /*
         * ⭐ Opname bertanda BEBAS: hasil hitung fisik bisa menambah maupun mengurangi. Yang disimpan
         * adalah **selisihnya** (hitungan − saldo teoretis), jadi tandanya sudah melekat di angkanya.
         * ⛔ Jangan menyimpan "saldo baru" — dua opname yang datang bersamaan akan saling menimpa
         * diam-diam, sementara dua selisih tetap menjumlah dengan benar.
         */
        case 'opname_adjustment':
            return 1;
    }
}
/**
 * ⭐ **Sasaran stok**: barang yang dijual (`product`) atau bahan yang tak pernah dijual
 * (`material`).
 *
 * ⛔ Bahan tak boleh jadi `product`: `product.tax_group_id` NOT NULL (K14) — tepung terigu tak
 * punya jawaban untuk "kena PB1 atau PPN". Dan tiap layar menu, katalog, serta kisi kasir harus
 * menyaringnya; yang lupa menyaring akan menampilkan "Minyak Goreng 5L" di depan tamu.
 */
export const STOCK_TARGETS = Object.freeze(['product', 'material']);
//# sourceMappingURL=stock.js.map